import { useNavigate } from 'react-router-dom'
import { Send, AlertTriangle, Info, Eye } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import type { Notificacion, TipoNotificacion } from '@/modules/notificaciones/types'

interface NotificacionItemProps {
  notificacion: Notificacion
  onMarcarLeida: (id: number) => void
}

function getIconForType(tipo: TipoNotificacion) {
  switch (tipo) {
    case 'campania_enviada':
      return { icon: Send, bgClass: 'bg-gray-100 text-gray-900', label: 'Enviada' }
    case 'campania_vencida':
      return { icon: AlertTriangle, bgClass: 'bg-gray-100 text-gray-900', label: 'Vencida' }
    case 'estado_cambiado':
      return { icon: Info, bgClass: 'bg-gray-100 text-gray-900', label: 'Cambio' }
    default:
      return { icon: Info, bgClass: 'bg-gray-100 text-gray-900', label: tipo }
  }
}

export function NotificacionItem({ notificacion, onMarcarLeida }: NotificacionItemProps) {
  const navigate = useNavigate()
  const { icon: Icon, label } = getIconForType(notificacion.tipo)

  const handleClick = () => {
    if (!notificacion.leida_por_usuario) {
      onMarcarLeida(notificacion.id)
    }
    // Navegar a la campaña
    const match = notificacion.url.match(/\/multichannel\/campania\/(\d+)\//)
    if (match) {
      navigate(`/multichannel/campania/${match[1]}`)
    }
  }

  return (
    <div
      className={`flex items-start gap-4 p-4 rounded-lg border cursor-pointer transition-colors hover:bg-muted/50 ${
        !notificacion.leida_por_usuario ? 'bg-gray-50 border-gray-300 dark:border-gray-700' : 'border-gray-300 dark:border-gray-700'
      }`}
      onClick={handleClick}
    >
      {/* Icono */}
      <Icon className="h-5 w-5 text-muted-foreground flex-shrink-0" />

      {/* Contenido */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-semibold text-sm">{notificacion.titulo}</span>
          {!notificacion.leida_por_usuario && (
            <span className="h-2 w-2 rounded-full bg-gray-900 flex-shrink-0" />
          )}
        </div>
        <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
          {notificacion.mensaje}
        </p>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <Badge variant="outline" className="text-xs">
            {label}
          </Badge>
          <span>{notificacion.campania.marca}</span>
          <span>
            {new Date(notificacion.fecha_creacion).toLocaleDateString('es-EC', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        </div>
      </div>

      {/* Acción marcar leída */}
      {!notificacion.leida_por_usuario && (
        <Button
          variant="ghost"
          size="icon"
          className="flex-shrink-0"
          onClick={(e) => {
            e.stopPropagation()
            onMarcarLeida(notificacion.id)
          }}
          title="Marcar como leída"
        >
          <Eye className="h-4 w-4" />
        </Button>
      )}
    </div>
  )
}
